/**
 * @fileoverview Date formatting: elements with vdx-date-* attributes get their textContent set to formatted date parts (day, year, month, date, currentdate).
 */

import { logError, handleError } from './errors.js';

const MODULE = 'date';

const ATTR_DAY = 'vdx-date-day';
const ATTR_YEAR = 'vdx-date-year';
const ATTR_MONTH = 'vdx-date-month';
const ATTR_DATE = 'vdx-date-date';
const ATTR_CURRENTDATE = 'vdx-date-currentdate';
const ATTR_LANG = 'vdx-date-lang';

const SELECTOR = `[${ATTR_DAY}], [${ATTR_YEAR}], [${ATTR_MONTH}], [${ATTR_DATE}], [${ATTR_CURRENTDATE}]`;
const DEFAULT_CURRENTDATE_FORMAT = 'MMMM d, yyyy';

/**
 * Returns the ordinal suffix for a day of the month (e.g. 1 → "st", 2 → "nd", 3 → "rd", 4 → "th").
 * @param {number} n - Day 1–31.
 * @returns {'st' | 'nd' | 'rd' | 'th'}
 */
function getDateSuffix(n) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'st';
    if (mod10 === 2 && mod100 !== 12) return 'nd';
    if (mod10 === 3 && mod100 !== 13) return 'rd';
    return 'th';
}

/**
 * Applies Microsoft-style format codes (d, dd, M, MM, MMM, MMMM, y, yy, yyyy, etc.) to a date.
 * @param {Date} date
 * @param {string} format - Format string with placeholders.
 * @returns {string}
 */
function formatWithCodes(date, format) {
    const map = {
        d: date.getDate(),
        dd: String(date.getDate()).padStart(2, '0'),
        ddd: date.toLocaleString('default', { weekday: 'short' }),
        dddd: date.toLocaleString('default', { weekday: 'long' }),
        M: date.getMonth() + 1,
        MM: String(date.getMonth() + 1).padStart(2, '0'),
        MMM: date.toLocaleString('default', { month: 'short' }),
        MMMM: date.toLocaleString('default', { month: 'long' }),
        y: date.getFullYear(),
        yy: String(date.getFullYear()).slice(-2),
        yyyy: date.getFullYear(),
    };
    return String(format).replace(/d{1,4}|M{1,4}|y{2,4}/g, (match) => (map[match] != null ? String(map[match]) : match));
}

/**
 * Resolves which vdx-date-* attribute is present on the element and returns its name and value.
 * @param {Element} element
 * @returns {{ type: string, format: string | null, lang: string | null } | null}
 */
function getDateAttr(element) {
    if (!element || !element.hasAttribute) return null;
    const lang = element.getAttribute(ATTR_LANG);
    if (element.hasAttribute(ATTR_DAY)) return { type: ATTR_DAY, format: element.getAttribute(ATTR_DAY), lang };
    if (element.hasAttribute(ATTR_YEAR)) return { type: ATTR_YEAR, format: element.getAttribute(ATTR_YEAR), lang };
    if (element.hasAttribute(ATTR_MONTH)) return { type: ATTR_MONTH, format: element.getAttribute(ATTR_MONTH), lang };
    if (element.hasAttribute(ATTR_DATE)) return { type: ATTR_DATE, format: element.getAttribute(ATTR_DATE), lang };
    if (element.hasAttribute(ATTR_CURRENTDATE)) return { type: ATTR_CURRENTDATE, format: element.getAttribute(ATTR_CURRENTDATE), lang };
    return null;
}

/**
 * Generates formatted content for the given attribute type and options.
 * @param {Date} date
 * @param {string} type - One of ATTR_DAY, ATTR_YEAR, ATTR_MONTH, ATTR_DATE, ATTR_CURRENTDATE.
 * @param {string | null} format - Attribute value (format hint).
 * @param {string | null} lang - vdx-date-lang value.
 * @returns {string}
 */
function generateContent(date, type, format, lang) {
    const locale = lang && String(lang).trim() !== '' ? lang.trim() : 'default';
    const fmt = format != null ? String(format).trim() : null;

    switch (type) {
        case ATTR_DAY: {
            const dayName = date.toLocaleString(locale, { weekday: 'long' });
            if (fmt === 'short') return dayName.slice(0, 3);
            if (fmt === 'narrow') return dayName.slice(0, 2);
            return dayName;
        }
        case ATTR_YEAR:
            return fmt === '2-digit' ? String(date.getFullYear()).slice(-2) : String(date.getFullYear());
        case ATTR_MONTH:
            return date.toLocaleString(locale, { month: fmt || 'long' });
        case ATTR_DATE:
            if (fmt === 'suffix') {
                const d = date.getDate();
                return `${d}${getDateSuffix(d)}`;
            }
            return fmt === '2-digit' ? String(date.getDate()).padStart(2, '0') : String(date.getDate());
        case ATTR_CURRENTDATE:
            return formatWithCodes(date, fmt || DEFAULT_CURRENTDATE_FORMAT);
        default:
            return '';
    }
}

/**
 * Applies date formatting to one element: detects attribute, generates content, sets textContent.
 * @param {Element} element
 * @param {Date} date
 */
function applyToElement(element, date) {
    const attr = getDateAttr(element);
    if (!attr) return;
    try {
        const content = generateContent(date, attr.type, attr.format, attr.lang);
        if (typeof element.textContent !== 'undefined') element.textContent = content;
    } catch (err) {
        handleError(`Attribute ${attr.type} value generated an error. Check format/value.`, {
            module: MODULE,
            element,
            showOnElement: true,
            error: err,
        });
    }
}

/**
 * Initializes date formatting: all elements with vdx-date-day, -year, -month, -date, or -currentdate get formatted content.
 */
export function formatDate() {
    if (typeof document === 'undefined') return;
    const date = new Date();
    let elements;
    try {
        elements = document.querySelectorAll(SELECTOR);
    } catch (e) {
        logError('Failed to query vdx-date-* elements.', { module: MODULE, error: e });
        return;
    }
    elements.forEach((el) => applyToElement(el, date));
}
